import { useNavigate } from 'react-router-dom';
import { tripsApi, type Trip } from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { PortalLayout } from '@/components/Layout';
import { PortalNav } from '@/components/Nav';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { formatDateTime, formatPeso } from '@/lib/format';

export function PortalTrips() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canBook = hasPermission('portal.book');
  const canSeeAmounts = hasPermission('portal.book') || hasPermission('portal.view_trips');
  const { data: trips, loading, error, reload } = useAsync(() => tripsApi.list(), []);

  const columns = [
    { key: 'reference', header: 'Reference' },
    { key: 'origin', header: 'Origin' },
    { key: 'destination', header: 'Destination' },
    {
      key: 'scheduledDate',
      header: 'Date',
      render: (row: Trip) => formatDateTime(row.scheduledDate),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row: Trip) => <StatusBadge status={row.status} size="sm" audience="client" />,
    },
    ...(canSeeAmounts
      ? [
          {
            key: 'totalCharges',
            header: 'Amount',
            align: 'right' as const,
            render: (row: Trip) => formatPeso(row.totalCharges),
          },
        ]
      : []),
  ];

  return (
    <PortalLayout>
      <div className="page">
        <div className="portal-nav">
          <PortalNav variant="top" />
        </div>

        <div className="page-header row-between">
          <div>
            <h1>Your trips</h1>
            <p className="text-muted">
              {canBook
                ? 'Book deliveries and track their progress'
                : 'Track your deliveries and see proof of delivery'}
            </p>
          </div>
          <div className="row">
            <Button variant="secondary" onClick={() => reload()}>Refresh</Button>
            {canBook && <Button onClick={() => navigate('/portal/book')}>Book trip</Button>}
          </div>
        </div>

        {error && (
          <div className="form-error" role="alert" style={{ marginBottom: 'var(--space-md)' }}>
            {error.includes('organisation')
              ? "Your login isn't linked to a company account yet. Please contact your account manager."
              : error}
          </div>
        )}

        <DataTable
          columns={columns}
          data={trips ?? []}
          keyField="id"
          loading={loading}
          loadError={error ? 'Trips could not be loaded. Use Refresh to try again.' : null}
          onRowClick={(row) => navigate(`/portal/trips/${row.id}`)}
          emptyMessage={
            error
              ? 'Trips could not be loaded.'
              : canBook
                ? 'No trips yet. Book your first delivery to get started.'
                : 'No trips yet.'
          }
        />
      </div>

      <style>{`
        .portal-nav { margin-bottom: var(--space-lg); }
        .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          font-size: 0.875rem;
        }
      `}</style>
    </PortalLayout>
  );
}
